/**
 * Shared GWP (Gift With Purchase) cart utilities.
 * Single source of truth for GWP evaluation and cart API execution.
 *
 * Consumers:
 *  - product-add-to-cart.js       → getItemsToBundle() for pre-bundling into POST
 *  - component-cart-items.js      → changeQtyWithGwp() batches qty change + GWP add/remove
 *  - component-gwp-progressbar.js → addGwp / removeGwp / updateGwpQty for reactive handling
 *
 * Session storage (`bww_gwp_dismissed`):
 *  - When a customer manually removes a GWP, its variant ID is stored so
 *    recalculation will not re-add it during the same browser session.
 *  - Cleared when the cart is emptied or checkout completes (empty cart).
 *
 * Exposed as window.GwpCartUtils (IIFE — works with both ES modules and plain scripts).
 */
(function () {
  "use strict";

  const DISMISSED_STORAGE_KEY = "bww_gwp_dismissed";

  /** @type {Array<{variantId:number, threshold:number, tier:string, cartPriceType:string}>|null} */
  let _cachedConfigs = null;
  /** @type {number} */
  let _minThreshold = Infinity;

  /** Read GWP tier configurations from DOM elements (cached per page lifecycle). */
  function getTierConfigs() {
    if (_cachedConfigs) return _cachedConfigs;

    const configs = [];
    let min = Infinity;
    /** @type {NodeListOf<HTMLElement>} */
    const els = document.querySelectorAll("[data-gwp-variant-id][data-gwp-target-threshold]");
    for (const el of els) {
      const variantId = parseInt(el.dataset.gwpVariantId, 10);
      const threshold = parseInt(el.dataset.gwpTargetThreshold, 10);
      const tier = el.dataset.gwpTier || "1";
      const cartPriceType = el.dataset.cartPriceType || "total_price";
      if (variantId && threshold) {
        configs.push({ variantId, threshold, tier, cartPriceType });
        if (threshold < min) min = threshold;
      }
    }

    _minThreshold = min;
    _cachedConfigs = configs;
    return configs;
  }

  /** Invalidate cached configs (called after section morphs that may change the DOM). */
  function invalidateCache() {
    _cachedConfigs = null;
    _minThreshold = Infinity;
  }

  /** Resolve cart total based on the price type the merchant configured. */
  function getCartTotal(cart, priceType) {
    if (!cart) return 0;
    if (priceType === "original_total_price") {
      return typeof cart.original_total_price === "number" ? cart.original_total_price : 0;
    }
    return typeof cart.total_price === "number" ? cart.total_price : 0;
  }

  /**
   * Find a GWP item in the cart by variant ID. Returns { item, line, key } or null.
   *
   * `key` is what the mutations below address, not `line`. A line number is only
   * true of the cart snapshot it was read from: anything that adds a line in the
   * meantime shifts every position after it, and a change request still holding
   * the old number then hits whatever moved into that slot. That is how a
   * concurrent add — a bundle repair, a drawer upsell, another tab — ends up
   * having a paid item deleted by the gift logic. `line` is kept only for the
   * no-utils fallback path in `component-gwp-progressbar.js`.
   */
  function findGwpInCart(cart, variantId) {
    if (!cart?.items?.length) return null;
    const idx = cart.items.findIndex(it => it.variant_id === variantId && isGwpProperty(it.properties));
    return idx === -1 ? null : { item: cart.items[idx], line: idx + 1, key: cart.items[idx].key };
  }

  function isGwpProperty(props) {
    if (!props) return false;
    if (props._isGWP !== undefined) {
      return ["true", true, 1, "1"].includes(props._isGWP);
    }
    if (Array.isArray(props)) {
      const p = props.find(x => (x?.name || x?.first) === "_isGWP");
      const v = p && (p.value ?? p.last);
      return ["true", true, 1, "1"].includes(v);
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Dismissed GWP (session storage) — user manually removed the free gift
  // ---------------------------------------------------------------------------

  /** @returns {number[]} */
  function getDismissedVariantIds() {
    try {
      const raw = window.sessionStorage?.getItem(DISMISSED_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(id => parseInt(id, 10)).filter(id => Number.isFinite(id) && id > 0);
    } catch {
      return [];
    }
  }

  /**
   * Persist that the customer manually removed this GWP variant.
   * @param {number|string} variantId
   */
  function dismissGwp(variantId) {
    const id = parseInt(/** @type {string} */ (variantId), 10);
    if (!Number.isFinite(id) || id <= 0) return;

    const ids = getDismissedVariantIds();
    if (ids.includes(id)) return;
    ids.push(id);

    try {
      window.sessionStorage?.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // sessionStorage unavailable (private mode quota, etc.)
    }
  }

  /** @param {number|string} variantId */
  function isGwpDismissed(variantId) {
    const id = parseInt(/** @type {string} */ (variantId), 10);
    if (!Number.isFinite(id) || id <= 0) return false;
    return getDismissedVariantIds().includes(id);
  }

  /** Clear all dismissed GWP flags (cart empty / checkout complete). */
  function clearDismissedGwp() {
    try {
      window.sessionStorage?.removeItem(DISMISSED_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * Clear dismissed flags when the cart is empty (covers cart clear + post-checkout).
   * @param {Object|null|undefined} cart
   */
  function clearDismissedIfCartEmpty(cart) {
    if (!cart) return;
    if (typeof cart.item_count === "number" && cart.item_count === 0) {
      clearDismissedGwp();
      return;
    }
    if (Array.isArray(cart.items) && cart.items.length === 0) {
      clearDismissedGwp();
    }
  }

  /** Collect cart section IDs from the page for bundling into POST requests. */
  function getSectionIds() {
    const ids = new Set();
    document.querySelectorAll("cart-items-component").forEach(el => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return ids;
  }

  /**
   * Retrieve the current cart object from BWW state.
   * Returns null when BWW or cart state isn't available yet.
   */
  function getCart() {
    const BWW = /** @type {any} */ (window).BWW;
    return BWW?.getCart ? BWW.getCart() : null;
  }

  // ---------------------------------------------------------------------------
  // Evaluation
  // ---------------------------------------------------------------------------

  /**
   * Get GWP items that should be pre-bundled into a cart/add.js payload.
   * Call this BEFORE the POST so both the product and GWP land in one request.
   *
   * Fast-exits when the result is obviously empty:
   *  - No GWP tiers configured → []
   *  - Estimated total won't reach lowest threshold → []
   *  - Every GWP variant already in cart → []
   *
   * @param {number} [addingPrice=0] - Price (cents) of the item(s) about to be added
   * @returns {Array<{id: number, quantity: number, properties: {_isGWP: string, _gwp_tier: string}}>}
   */
  function getItemsToBundle(addingPrice) {
    const configs = getTierConfigs();
    if (configs.length === 0) return [];

    const cart = getCart();
    if (!cart) return [];

    clearDismissedIfCartEmpty(cart);

    // Fast path: estimated total can't reach even the lowest threshold
    const currentTotal = getCartTotal(cart, configs[0].cartPriceType);
    if (currentTotal + (addingPrice || 0) < _minThreshold) return [];

    // Fast path: every GWP variant is already in the cart
    if (configs.every(cfg => findGwpInCart(cart, cfg.variantId))) return [];

    const items = [];
    for (const cfg of configs) {
      const estimated = getCartTotal(cart, cfg.cartPriceType) + (addingPrice || 0);
      if (estimated < cfg.threshold) continue;
      if (findGwpInCart(cart, cfg.variantId)) continue;
      if (isGwpDismissed(cfg.variantId)) continue;
      if (items.some(i => i.id === cfg.variantId)) continue;

      items.push({
        id: cfg.variantId,
        quantity: 1,
        properties: { _isGWP: "true", _gwp_tier: cfg.tier },
      });
    }
    return items;
  }

  /**
   * Evaluate the full GWP state for a given cart.
   * Returns the list of actions the caller should take.
   *
   * Fast-exits when no action is possible:
   *  - No GWP tiers configured → []
   *  - Cart total below lowest threshold AND no GWP items in cart → []
   *    (nothing to add, nothing to remove)
   *
   * @param {Object} cart
   * @returns {Array<{action:'add'|'remove'|'qty', variantId:number, tier:string, line?:number, quantity?:number}>}
   */
  function evaluate(cart) {
    if (!cart) return [];

    clearDismissedIfCartEmpty(cart);

    const configs = getTierConfigs();
    if (configs.length === 0) return [];

    // Fast path: below every threshold AND no GWP items to clean up
    const total = getCartTotal(cart, configs[0].cartPriceType);
    if (total < _minThreshold && !configs.some(cfg => findGwpInCart(cart, cfg.variantId))) {
      return [];
    }

    const actions = [];
    for (const cfg of configs) {
      const cfgTotal = getCartTotal(cart, cfg.cartPriceType);
      const meets = cfgTotal >= cfg.threshold;
      const gwp = findGwpInCart(cart, cfg.variantId);
      const has = !!gwp;
      const qty = gwp?.item?.quantity || 0;

      if (meets && !has && !isGwpDismissed(cfg.variantId)) {
        actions.push({ action: "add", variantId: cfg.variantId, tier: cfg.tier });
      } else if (!meets && has) {
        actions.push({ action: "remove", variantId: cfg.variantId, tier: cfg.tier, line: gwp.line, key: gwp.key });
      } else if (meets && has && qty !== 1) {
        actions.push({
          action: "qty",
          variantId: cfg.variantId,
          tier: cfg.tier,
          line: gwp.line,
          key: gwp.key,
          quantity: 1,
        });
      }
    }
    return actions;
  }

  // ---------------------------------------------------------------------------
  // Execution — single-request cart mutations with sections bundled
  // ---------------------------------------------------------------------------

  function buildSectionsParams() {
    const ids = getSectionIds();
    if (ids.size === 0) return {};
    return {
      sections: Array.from(ids).join(","),
      sections_url: window.location.pathname,
    };
  }

  /**
   * POST /cart/add.js with sections bundled.
   * @returns {Promise<{data: Object, sections: Object}>}
   */
  async function addGwp(variantId, tier) {
    if (isGwpDismissed(variantId)) {
      return { data: null, sections: {} };
    }

    const body = {
      items: [{ id: variantId, quantity: 1, properties: { _isGWP: "true", _gwp_tier: tier } }],
      ...buildSectionsParams(),
    };

    const res = await fetch(/** @type {any} */ (window).Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || "GWP add failed");
    }
    const data = await res.json();
    return { data, sections: data.sections || {} };
  }

  /**
   * POST /cart/change.js to remove a GWP (qty → 0), with sections bundled.
   * @param {string} key - The GWP's line item key, from `findGwpInCart`
   * @returns {Promise<{data: Object, sections: Object}>}
   */
  async function removeGwp(key) {
    return changeGwpLine(key, 0);
  }

  /**
   * POST /cart/change.js to adjust GWP quantity, with sections bundled.
   * @param {string} key - The GWP's line item key, from `findGwpInCart`
   * @returns {Promise<{data: Object, sections: Object}>}
   */
  async function updateGwpQty(key, quantity) {
    return changeGwpLine(key, quantity);
  }

  /**
   * @private shared helper for change requests
   *
   * Addressed by line item key rather than position, so a line added between
   * reading the cart and sending this request cannot redirect it at the wrong
   * item. See `findGwpInCart`.
   */
  async function changeGwpLine(key, quantity) {
    const body = { id: key, quantity, ...buildSectionsParams() };

    const res = await fetch(/** @type {any} */ (window).Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || "GWP change failed");
    }
    const data = await res.json();
    return { data, sections: data.sections || {} };
  }

  /**
   * After a GWP mutation, push the fresh cart into BWW state and morph
   * sections via a cart:update event so the UI updates in one pass —
   * no extra GET /cart.js or sections fetch needed.
   *
   * @param {Object} cartData  - Full cart object returned by /cart/change.js
   *                              (or null for /cart/add.js which returns items, not cart)
   * @param {Object} sections  - Section HTML map from the response
   */
  function dispatchCartUpdate(cartData, sections) {
    // Section morphs may change the DOM, so invalidate the cached tier configs
    invalidateCache();

    if (cartData && cartData.items !== undefined && cartData.item_count !== undefined) {
      clearDismissedIfCartEmpty(cartData);
      const BWW = /** @type {any} */ (window).BWW;
      if (BWW?.set) BWW.set("cartState", cartData);
    }

    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: { data: { sections: sections || {} } },
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Combo: qty change + GWP in fewer requests
  // ---------------------------------------------------------------------------

  /**
   * Combine a quantity change with any necessary GWP adjustments.
   *
   * REMOVAL case  → single POST /cart/update.js  (qty + GWP removal)
   * ADD case      → POST /cart/change.js then immediate POST /cart/add.js
   * No GWP change → returns null (caller should use the regular /cart/change.js)
   *
   * @param {number} line     - 1-based cart line number
   * @param {number} quantity - Desired new quantity for the item
   * @returns {Promise<{data: Object, sections: Object}|null>}
   */
  async function changeQtyWithGwp(line, quantity) {
    const cart = getCart();
    if (!cart?.items?.length) return null;

    const configs = getTierConfigs();
    if (configs.length === 0) return null;

    const item = cart.items[line - 1];
    if (!item) return null;

    const qtyDelta = quantity - item.quantity;
    if (qtyDelta === 0) return null;

    const removals = [];
    const adds = [];

    for (const cfg of configs) {
      const estimated = getCartTotal(cart, cfg.cartPriceType) + item.price * qtyDelta;
      const meets = estimated >= cfg.threshold;
      const gwp = findGwpInCart(cart, cfg.variantId);
      const has = !!gwp;

      if (!meets && has) removals.push({ variantId: cfg.variantId });
      else if (meets && !has && !isGwpDismissed(cfg.variantId)) {
        adds.push({ variantId: cfg.variantId, tier: cfg.tier });
      }
    }

    if (removals.length === 0 && adds.length === 0) return null;

    const root = /** @type {any} */ (window).Shopify.routes.root;
    const sp = buildSectionsParams();

    // ── REMOVAL ONLY → single /cart/update.js ──────────────────────────
    if (removals.length > 0 && adds.length === 0) {
      const dupes = cart.items.filter((/** @type {any} */ i) => i.variant_id === item.variant_id).length;

      if (dupes > 1) {
        // Variant appears on multiple lines — use line-based change first
        await fetch(root + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line, quantity }),
        });
        /** @type {Record<string, number>} */
        const upd = {};
        for (const r of removals) upd[r.variantId] = 0;
        const res = await fetch(root + "cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: upd, ...sp }),
        });
        const data = await res.json();
        return { data, sections: data.sections || {} };
      }

      /** @type {Record<string, number>} */
      const updates = {};
      updates[item.variant_id] = quantity;
      for (const r of removals) updates[r.variantId] = 0;

      const res = await fetch(root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates, ...sp }),
      });
      const data = await res.json();
      return { data, sections: data.sections || {} };
    }

    // ── ADD (possibly with removals) → change first, then immediate add ─
    const changeRes = await fetch(root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line, quantity }),
    });
    const changeData = await changeRes.json();
    if (changeData.errors) return { data: changeData, sections: {} };

    if (removals.length > 0) {
      /** @type {Record<string, number>} */
      const upd = {};
      for (const r of removals) upd[r.variantId] = 0;
      await fetch(root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: upd }),
      });
    }

    const addItems = adds.map((/** @type {any} */ a) => ({
      id: a.variantId,
      quantity: 1,
      properties: { _isGWP: "true", _gwp_tier: a.tier },
    }));

    const addRes = await fetch(root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: addItems, ...sp }),
    });
    const addData = await addRes.json();
    return { data: addData, sections: addData.sections || {} };
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  window.GwpCartUtils = {
    getTierConfigs,
    getCartTotal,
    findGwpInCart,
    isGwpProperty,
    getSectionIds,
    getCart,
    getItemsToBundle,
    evaluate,
    changeQtyWithGwp,
    addGwp,
    removeGwp,
    updateGwpQty,
    dispatchCartUpdate,
    invalidateCache,
    dismissGwp,
    isGwpDismissed,
    clearDismissedGwp,
    clearDismissedIfCartEmpty,
    getDismissedVariantIds,
  };

  // On load: if Liquid already rendered an empty cart, clear any stale dismissals
  // (covers post-checkout return and hard-refresh with empty cart).
  try {
    const bodyCount = document.body?.dataset?.cartItemCount;
    if (bodyCount !== undefined && parseInt(bodyCount, 10) === 0) {
      clearDismissedGwp();
    }
  } catch {
    // ignore
  }
})();
